import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ChevronDown, ChevronUp, Search, ArrowRight } from 'lucide-react';

const FAQ = () => {
  const [openQuestion, setOpenQuestion] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const faqCategories = [
    {
      category: 'Programs Overview',
      questions: [
        {
          id: 1,
          question: 'What programs does GIHDS offer?',
          answer: 'The Geneva Institute of Humanitarian & Development Studies (GIHDS) offers four fully online programs: the Master of Humanitarian Action & Development (1 year), the Master of Development Project & Program Management (9 months), the Doctor of Humanitarian Action & Development (3 years), and a short professional course — AI in Humanitarian Action & Development (4 weeks).'
        },
        {
          id: 2,
          question: 'How long is the Master of Humanitarian Action & Development?',
          answer: 'One year (12 months) in total: 9 monthly courses (Foundations of Humanitarian Action, Humanitarian Principles & International Law, Program & Project Management in NGOs, MEAL, Emergency Response & Disaster Management, Donor Systems & Proposal Development, Protection Gender & Inclusion, Logistics & Operations, and Leadership & Coordination), followed by a 3-month Capstone Field Project.'
        },
        {
          id: 3,
          question: 'How long is the Master of Development Project & Program Management?',
          answer: 'Nine months in total: 5 monthly courses (Development Project Management, Development Program Management, Development Project Finance, Development MEAL, Theory of Change) followed by a 4-month Capstone Field Project.'
        },
        {
          id: 4,
          question: 'How long is the Doctor of Humanitarian Action & Development?',
          answer: 'Three years in total: 8 advanced courses covering theory, research methods, policy analysis, international humanitarian law, strategic leadership, quantitative & qualitative research design, research ethics, and global health, followed by a 12-month Doctoral Dissertation.'
        },
        {
          id: 5,
          question: 'What is the AI in Humanitarian Action & Development course?',
          answer: 'A 4-week short professional course that teaches practitioners how to use AI responsibly across the full humanitarian and development program cycle — needs assessment, program design, donor mapping, MEAL, reporting, and daily productivity. It includes a Humanitarian & Development AI Prompt Bank with over 200 reusable prompts. No prior technical or AI experience is required.'
        },
        {
          id: 6,
          question: 'Are programs delivered online or in person?',
          answer: 'All GIHDS programs are delivered 100% online through our Learning Management System (LMS) at lms.gihds.org. You can study from anywhere in the world with a reliable internet connection.'
        },
        {
          id: 7,
          question: 'Do you have active learners in many countries?',
          answer: 'Yes. GIHDS has active learners in 20 countries across Africa and Asia: Afghanistan, Benin, C\u00f4te d\u2019Ivoire, Ethiopia, Ghana, Kenya, Malawi, Myanmar, Nepal, Nigeria, Philippines, Sierra Leone, Somalia, South Sudan, Sudan, Syria, Togo, Uganda, Zambia, and Zimbabwe.'
        }
      ]
    },
    {
      category: 'Admissions',
      questions: [
        {
          id: 8,
          question: 'What are the admission requirements for the Master\u2019s programs?',
          answer: 'For both Master\u2019s programs, applicants need a bachelor\u2019s degree or equivalent qualification, proficiency in English, access to a reliable internet connection, and commitment to completing the full program.'
        },
        {
          id: 9,
          question: 'What are the admission requirements for the Doctoral program?',
          answer: 'For the Doctor of Humanitarian Action & Development, you need a master\u2019s degree in a relevant field, professional experience in the humanitarian or development sector, proficiency in English, reliable internet access, and commitment to completing the 3-year research program.'
        },
        {
          id: 10,
          question: 'What are the requirements for the AI short course?',
          answer: 'The AI in Humanitarian Action & Development course is open to professionals, students, and practitioners in humanitarian, development, NGO, public-sector, or social-impact work. No prior technical, coding, or AI experience is required — only proficiency in English and reliable internet access.'
        },
        {
          id: 11,
          question: 'Is there an entrance exam?',
          answer: 'No, GIHDS does not require any entrance exam. Candidates are evaluated on their educational background, professional experience, and commitment to humanitarian and development work.'
        },
        {
          id: 12,
          question: 'What documents do I need to apply?',
          answer: 'A valid identification document (passport or national ID), your highest educational certificate, and the information requested by the online application form. A CV/resume and short motivation statement help strengthen your application.'
        },
        {
          id: 13,
          question: 'How long does the application review take?',
          answer: 'Applications are typically reviewed within 1\u20132 weeks. You will receive your admission decision — and, if applicable, your scholarship decision — by email.'
        }
      ]
    },
    {
      category: 'Scholarship',
      questions: [
        {
          id: 14,
          question: 'Who is eligible for the 90% scholarship?',
          answer: 'The GIHDS Global Scholarship Initiative offers a 90% tuition reduction to eligible applicants from African and Asian countries who demonstrate commitment to humanitarian and development work. Scholarship decisions are determined solely by GIHDS.'
        },
        {
          id: 15,
          question: 'Which programs does the scholarship apply to?',
          answer: 'The 90% scholarship applies to the three degree programs: Master of Humanitarian Action & Development (full fee USD 2,000), Master of Development Project & Program Management (full fee USD 3,500), and Doctor of Humanitarian Action & Development (full fee USD 10,800). The scholarship does not apply to the AI in Humanitarian Action & Development short course.'
        },
        {
          id: 16,
          question: 'When will I know the scholarship decision?',
          answer: 'Scholarship decisions are typically communicated within 2\u20133 weeks of submitting a complete application.'
        }
      ]
    },
    {
      category: 'Tuition & Fees',
      questions: [
        {
          id: 17,
          question: 'What is the tuition fee for the Master\u2019s programs?',
          answer: 'Master of Humanitarian Action & Development: USD 2,000 total (full program fee). Master of Development Project & Program Management: USD 3,500 total (full program fee). Eligible learners from Africa and Asia may apply for our 90% scholarship.'
        },
        {
          id: 18,
          question: 'What is the tuition fee for the Doctoral program?',
          answer: 'The full tuition for the Doctor of Humanitarian Action & Development is USD 10,800 for the entire 3-year program (USD 3,600 per year). Eligible learners from Africa and Asia may apply for our 90% scholarship.'
        },
        {
          id: 19,
          question: 'What is the fee for the AI short course?',
          answer: 'USD 99 for standard enrollment and USD 59 for current GIHDS students. The 90% scholarship does not apply to this short course.'
        },
        {
          id: 20,
          question: 'Are there any additional or hidden fees?',
          answer: 'No. The program fee covers full access to course content, the online LMS, faculty guidance, and — upon successful completion — the corresponding academic document. There are no hidden fees.'
        },
        {
          id: 21,
          question: 'What is your refund policy?',
          answer: 'GIHDS maintains a strict no-refund policy. Once payment has been made, no refunds are issued under any circumstances. Please review our Refund Policy page carefully before making any payment.'
        }
      ]
    },
    {
      category: 'Learning Experience',
      questions: [
        {
          id: 22,
          question: 'What learning resources are provided?',
          answer: 'Learners receive access to video lectures, reading materials, case studies, discussion forums, practical assignments, and direct faculty guidance through the LMS. The AI course also includes a Humanitarian & Development AI Prompt Bank with 200+ reusable prompts.'
        },
        {
          id: 23,
          question: 'How do I interact with faculty and other learners?',
          answer: 'The LMS includes discussion forums for peer interaction and direct messaging for faculty communication. Periodic live sessions and webinars are also organized during the program.'
        },
        {
          id: 24,
          question: 'What technical requirements do I need?',
          answer: 'A computer or tablet with a reliable internet connection, a modern web browser, and the ability to view video content. No specialized software is required.'
        },
        {
          id: 25,
          question: 'What academic document will I receive upon completion?',
          answer: 'On successful completion of all requirements, graduates receive: a Master\u2019s Degree Certificate and Academic Transcript for the Master of Humanitarian Action & Development and the Master of Development Project & Program Management; a Doctoral Degree Certificate and Academic Transcript for the Doctor of Humanitarian Action & Development; and a Certificate of Completion for the AI in Humanitarian Action & Development short course.'
        },
        {
          id: 26,
          question: 'Can I preview the LMS before enrolling?',
          answer: 'Yes. Visit lms.gihds.org and log in with Username: visitor2026@gihds.org and Password: Welcome2026 to explore the learning platform before enrolling.'
        }
      ]
    },
    {
      category: 'Contact & Support',
      questions: [
        {
          id: 27,
          question: 'How can I contact GIHDS?',
          answer: 'For admissions, scholarship, and program inquiries, please email admissions@gihds.org or use the contact form on the Contact page. Our team typically responds within 24\u201348 hours.'
        }
      ]
    }
  ];

  const filteredCategories = faqCategories.map(category => ({
    ...category,
    questions: category.questions.filter(
      q => q.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
           q.answer.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  return (
    <div className="min-h-screen bg-white" data-testid="faq-page">
      {/* Hero Section */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-32 bg-[#F1F5F8]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <p className="text-sm font-medium uppercase tracking-widest text-[#163A59] mb-4">
              Help Center
            </p>
            <h1 className="font-['Source_Serif_4'] text-4xl md:text-5xl lg:text-6xl font-bold text-slate-900 mb-6">
              Frequently Asked Questions
            </h1>
            <p className="text-lg md:text-xl text-slate-600 leading-relaxed mb-8">
              Find answers to common questions about our programs, admissions, 
              scholarships, and more.
            </p>

            {/* Search Bar */}
            <div className="relative max-w-xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" size={20} />
              <input
                type="text"
                placeholder="Search for answers..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full h-14 pl-12 pr-4 border border-[#DCE4EA] rounded-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#163A59] focus:border-transparent transition-all"
                data-testid="faq-search-input"
              />
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-20 md:py-32">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          {filteredCategories.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-slate-600">No questions found matching your search.</p>
            </div>
          ) : (
            <div className="space-y-12">
              {filteredCategories.map((category, categoryIndex) => (
                <motion.div
                  key={category.category}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: categoryIndex * 0.1 }}
                  viewport={{ once: true }}
                >
                  <h2 className="font-['Source_Serif_4'] text-2xl md:text-3xl font-semibold text-slate-900 mb-6 pb-4 border-b border-[#DCE4EA]">
                    {category.category}
                  </h2>
                  <div className="space-y-4">
                    {category.questions.map((item) => (
                      <div
                        key={item.id}
                        className="border border-[#DCE4EA] bg-white"
                      >
                        <button
                          onClick={() => setOpenQuestion(openQuestion === item.id ? null : item.id)}
                          className="w-full text-left p-6 flex items-start justify-between gap-4"
                          data-testid={`faq-question-${item.id}`}
                        >
                          <h3 className="font-medium text-slate-900 pr-4">
                            {item.question}
                          </h3>
                          <div className="text-slate-400 flex-shrink-0">
                            {openQuestion === item.id ? (
                              <ChevronUp size={20} />
                            ) : (
                              <ChevronDown size={20} />
                            )}
                          </div>
                        </button>
                        {openQuestion === item.id && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="px-6 pb-6"
                          >
                            <p className="text-slate-600 leading-relaxed">
                              {item.answer}
                            </p>
                          </motion.div>
                        )}
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Still Have Questions */}
      <section className="py-20 md:py-32 bg-[#163A59]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-['Source_Serif_4'] text-3xl md:text-4xl font-bold text-white mb-6">
              Still Have Questions?
            </h2>
            <p className="text-lg text-white/80 max-w-2xl mx-auto mb-10">
              Can't find the answer you're looking for? Our team is here to help. 
              Reach out to us and we'll get back to you within 24-48 hours.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/contact"
                className="inline-flex items-center justify-center gap-2 bg-white text-[#163A59] px-8 py-4 rounded-sm font-medium hover:bg-slate-100 transition-all"
                data-testid="faq-contact-btn"
              >
                Contact Us
                <ArrowRight size={18} />
              </Link>
              <Link
                to="/admissions"
                className="inline-flex items-center justify-center gap-2 bg-[#C82032] hover:bg-[#A81828] text-white px-8 py-4 rounded-sm font-medium transition-all"
                data-testid="faq-apply-btn"
              >
                Apply Now
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;
